import React from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useSquareCard } from "../lib/useSquareCard";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Checkbox } from "./ui/checkbox";
import { Badge } from "./ui/badge";
import { CreditCard, Repeat, ShieldCheck, XCircle, CheckCircle2 } from "lucide-react";

export default function SubscriptionCard() {
    const [config, setConfig] = React.useState(null);
    const [subs, setSubs] = React.useState([]);
    const [starting, setStarting] = React.useState(false);
    const [showForm, setShowForm] = React.useState(false);
    const [consent, setConsent] = React.useState(false);
    const [billingDay, setBillingDay] = React.useState(0); // 0 = use server default

    const load = React.useCallback(async () => {
        const [c, s] = await Promise.all([
            api.get("/config"),
            api.get("/subscriptions/me").catch(() => ({ data: [] })),
        ]);
        setConfig(c.data);
        setSubs(s.data);
        // Default the picker to the server-computed first Sunday.
        setBillingDay(prev => prev || c.data.default_billing_day || 6);
    }, []);
    React.useEffect(() => { load(); }, [load]);

    // Lazy-load Square Web Payments SDK when opening the form (real mode only).
    const { cardRef, cardReady, cardError, retry } = useSquareCard("sub-card-container", config, showForm);

    async function startSubscription() {
        if (!consent) return toast.error("Please confirm the auto-pay consent.");
        if (!config.square_mock_mode && !cardRef.current) {
            return toast.error("Card form is still loading, please wait a moment.");
        }
        setStarting(true);
        try {
            let sourceId = "mock_card_token";
            if (!config.square_mock_mode) {
                const t = await cardRef.current.tokenize();
                if (t.status !== "OK") throw new Error(t.errors?.[0]?.message || "Card verification failed");
                sourceId = t.token;
            }
            const { data } = await api.post("/subscriptions/start", { source_id: sourceId, consent: true, billing_day: billingDay });
            toast.success("Monthly auto-pay is ON", {
                description: `${data.child_count} child${data.child_count === 1 ? "" : "ren"} · $${(data.monthly_cents / 100).toFixed(2)}/month`,
                icon: <Repeat className="w-4 h-4" />,
            });
            setShowForm(false); setConsent(false);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message || "Could not start subscription");
        } finally { setStarting(false); }
    }

    async function cancelSubscription(sid) {
        if (!window.confirm("Cancel monthly auto-pay? You can restart it any time.")) return;
        try {
            await api.post(`/subscriptions/${sid}/cancel`);
            toast.success("Monthly auto-pay canceled");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Cancel failed");
        }
    }

    if (!config) return null;
    const active = subs.find(s => s.status === "ACTIVE" || s.status === "PENDING");
    const monthlyFee = (config.monthly_fee_cents / 100).toFixed(2);

    return (
        <Card className="border-primary/20 bg-primary/[0.03]">
            <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary grid place-items-center">
                        <Repeat className="w-5 h-5" />
                    </div>
                    <div>
                        <div className="overline text-brand-stone">Monthly tuition</div>
                        <div className="font-dash text-lg font-semibold">
                            ${monthlyFee}/month per child · auto-pay
                        </div>
                    </div>
                </div>

                {active ? (
                    <div className={`rounded-lg bg-card border p-4 ${active.status === "ACTIVE" ? "border-emerald-200" : "border-amber-200"}`}>
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                            <div>
                                <Badge className={`border-0 gap-1 ${active.status === "ACTIVE" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                                    <CheckCircle2 className="w-3 h-3" /> {active.status}
                                </Badge>
                                <div className="mt-2 font-dash text-lg">
                                    ${(active.monthly_cents / 100).toFixed(2)}/month
                                    <span className="text-sm text-brand-stone"> · {active.child_count} child{active.child_count === 1 ? "" : "ren"}</span>
                                </div>
                                <div className="text-xs text-brand-stone mt-1 flex items-center gap-2 flex-wrap">
                                    <CreditCard className="w-3 h-3" />
                                    {active.card_brand || "Card"} ending in {active.card_last_4 || "••••"}
                                    {active.next_billing_at && (
                                        <span>· {active.status === "PENDING" ? "Starts" : "Next billing"} {new Date(active.next_billing_at).toLocaleDateString()}</span>
                                    )}
                                    {active.mock && <span className="italic">· sandbox mock</span>}
                                </div>
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                data-testid="cancel-subscription-btn"
                                onClick={() => cancelSubscription(active.id)}
                                className="gap-2 text-destructive border-destructive/40 hover:bg-destructive/10">
                                <XCircle className="w-4 h-4" /> Cancel auto-pay
                            </Button>
                        </div>
                    </div>
                ) : !showForm ? (
                    <div>
                        <p className="text-sm text-brand-stone leading-relaxed">
                            Set up auto-pay so ${monthlyFee}/month per child is billed automatically each month —
                            never miss a Sunday.
                        </p>
                        <Button
                            className="mt-4 bg-primary hover:bg-primary/90 gap-2"
                            data-testid="start-subscription-btn"
                            onClick={() => setShowForm(true)}>
                            <Repeat className="w-4 h-4" /> Turn on monthly auto-pay
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="rounded-lg border border-primary/20 bg-primary/[0.03] p-3">
                            <div className="text-xs uppercase tracking-wider text-brand-stone mb-2">Billing day</div>
                            <div className="text-sm text-brand-stone mb-2">
                                Your card is charged on this day of every month (1st–7th).
                            </div>
                            <div className="flex gap-1.5 flex-wrap">
                                {[1, 2, 3, 4, 5, 6, 7].map(d => (
                                    <button
                                        type="button"
                                        key={d}
                                        onClick={() => setBillingDay(d)}
                                        data-testid={`billing-day-${d}`}
                                        className={`w-10 h-10 rounded-full text-sm font-medium transition ${billingDay === d
                                            ? "bg-primary text-primary-foreground"
                                            : "bg-white border border-border hover:border-primary/40"}`}
                                    >
                                        {d}
                                    </button>
                                ))}
                            </div>
                            <div className="text-[11px] text-brand-stone mt-2 italic">
                                Default is the 1st Sunday of the month (day {config.default_billing_day || 1}).
                            </div>
                        </div>
                        {config.square_mock_mode ? (
                            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                                <strong>Sandbox mock mode.</strong> Once your Square keys are configured, this dialog
                                will show a real card entry. For now, clicking below will simulate a saved card.
                            </div>
                        ) : (
                            <div>
                                <div className="text-xs text-brand-stone mb-2">Card details (Square)</div>
                                <div id="sub-card-container" className="min-h-[80px] relative">
                                    {!cardReady && !cardError && (
                                        <div className="absolute inset-0 grid place-items-center pointer-events-none">
                                            <div className="text-sm text-brand-stone animate-pulse">Loading secure card form…</div>
                                        </div>
                                    )}
                                </div>
                                {cardError && (
                                    <div className="mt-3 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800 flex items-center justify-between gap-3">
                                        <span>{cardError}. Please try again.</span>
                                        <Button size="sm" variant="outline" onClick={retry} data-testid="sub-retry-card-btn">Retry</Button>
                                    </div>
                                )}
                            </div>
                        )}
                        <label className="flex items-start gap-3 text-sm text-brand-ink cursor-pointer">
                            <Checkbox checked={consent} onCheckedChange={setConsent} data-testid="subscription-consent" />
                            <span>
                                I authorize the Islamic Center of Federal Way to charge my card{" "}
                                <strong>${monthlyFee}</strong> per child every month, until I cancel.
                            </span>
                        </label>
                        <div className="flex items-center gap-2">
                            <Button
                                onClick={startSubscription}
                                disabled={starting || !consent || (!config.square_mock_mode && !cardReady)}
                                data-testid="confirm-subscription-btn"
                                className="bg-primary hover:bg-primary/90 gap-2">
                                <ShieldCheck className="w-4 h-4" />
                                {starting ? "Starting…" : "Start auto-pay"}
                            </Button>
                            <Button variant="ghost" onClick={() => { setShowForm(false); setConsent(false); }}>
                                Cancel
                            </Button>
                        </div>
                    </div>
                )}

                {subs.filter(s => s.status === "CANCELED").length > 0 && (
                    <details className="mt-5 text-xs text-brand-stone">
                        <summary className="cursor-pointer hover:text-primary">Past subscriptions</summary>
                        <ul className="mt-2 space-y-1">
                            {subs.filter(s => s.status === "CANCELED").map(s => (
                                <li key={s.id}>
                                    ${(s.monthly_cents / 100).toFixed(2)}/mo · canceled {s.canceled_at?.slice(0, 10)}
                                </li>
                            ))}
                        </ul>
                    </details>
                )}
            </CardContent>
        </Card>
    );
}
